import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { posterUrl } from "@/lib/seminars";

const EMPTY = {
  name: "", description: "", speaker: "", location: "", cfu: "1", date_from: "", date_to: "",
  start_time: "", end_time: "", external_price: "", external_seats: "", lunch_price: "", public_booking: false, seminar_kind: "seminario",
};
const num = (v) => (v === "" || v == null ? null : Number(v));

function PosterField({ preview, onPick }) {
  return (
    <label className="block cursor-pointer group" data-testid="seminar-poster-field">
      <div className="aspect-[4/5] w-full rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 overflow-hidden grid place-items-center group-hover:border-[#E2725B] transition-colors">
        {preview ? <img src={preview} alt="Locandina" className="w-full h-full object-cover"/> : (
          <div className="text-center text-xs text-stone-500 p-4"><ImagePlus size={24} className="mx-auto mb-1.5"/>Locandina 4:5<br/>(es. 1080×1350)</div>
        )}
      </div>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid="seminar-poster-input"/>
    </label>
  );
}

export default function SeminarFormDialog({ open, onOpenChange, seminar }) {
  const qc = useQueryClient();
  const [f, setF] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!open) return;
    setFile(null);
    if (seminar) {
      setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, seminar[k] ?? EMPTY[k]])) });
      setPreview(seminar.poster ? posterUrl(seminar) : null);
    } else { setF(EMPTY); setPreview(null); }
  }, [open, seminar]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...f, cfu: num(f.cfu), external_price: num(f.external_price), external_seats: num(f.external_seats), lunch_price: num(f.lunch_price),
        date_from: f.date_from || null, date_to: f.date_to || f.date_from || null,
        start_time: f.start_time || null, end_time: f.end_time || null, is_seminar: true,
      };
      const s = seminar ? (await api.patch(`/courses/${seminar.id}`, body)).data : (await api.post("/courses", body)).data;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/courses/${s.id}/poster`, fd);
      }
      return s;
    },
    onSuccess: () => { toast.success(seminar ? "Seminario aggiornato" : "Seminario creato"); qc.invalidateQueries({ queryKey: ["seminars"] }); onOpenChange(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const invalidDates = f.date_to && f.date_from && f.date_to < f.date_from;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="seminar-form-dialog">
        <DialogHeader>
          <DialogTitle>{seminar ? "Modifica seminario" : "Nuovo seminario"}</DialogTitle>
          <DialogDescription>Locandina, date, orari e quota per gli esterni.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-5">
          <PosterField preview={preview} onPick={(fl) => { setFile(fl); if (fl) setPreview(URL.createObjectURL(fl)); }}/>
          <div className="space-y-3">
            <div className="grid grid-cols-[140px_1fr] gap-3">
              <div><Label className="text-xs">Tipo</Label>
                <select value={f.seminar_kind || "seminario"} onChange={set("seminar_kind")} data-testid="seminar-kind" className="mt-1 w-full h-9 rounded-md border border-input bg-transparent px-2 text-sm">
                  <option value="seminario">Seminario</option><option value="workshop">Workshop</option>
                </select></div>
              <div><Label className="text-xs">Titolo *</Label><Input value={f.name} onChange={set("name")} className="mt-1" data-testid="seminar-name"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Relatore</Label><Input value={f.speaker} onChange={set("speaker")} className="mt-1" data-testid="seminar-speaker"/></div>
              <div><Label className="text-xs">Luogo</Label><Input value={f.location} onChange={set("location")} className="mt-1" data-testid="seminar-location"/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Dal giorno</Label><Input type="date" value={f.date_from} onChange={set("date_from")} className="mt-1" data-testid="seminar-date-from"/></div>
              <div><Label className="text-xs">Al giorno</Label><Input type="date" min={f.date_from} value={f.date_to} onChange={set("date_to")} className="mt-1" data-testid="seminar-date-to"/></div>
              <div><Label className="text-xs">Dalle ore</Label><Input type="time" value={f.start_time} onChange={set("start_time")} className="mt-1" data-testid="seminar-start-time"/></div>
              <div><Label className="text-xs">Alle ore</Label><Input type="time" value={f.end_time} onChange={set("end_time")} className="mt-1" data-testid="seminar-end-time"/></div>
            </div>
            {invalidDates && <div className="text-xs text-red-600">La data di fine è precedente all'inizio.</div>}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label className="text-xs">CFU studenti</Label><Input type="number" step="0.5" min="0" value={f.cfu} onChange={set("cfu")} className="mt-1" data-testid="seminar-cfu"/></div>
              <div><Label className="text-xs">Costo esterni (€)</Label><Input type="number" step="0.01" min="0" value={f.external_price} onChange={set("external_price")} placeholder="0 = gratuito" className="mt-1" data-testid="seminar-price"/></div>
              <div><Label className="text-xs">Posti esterni</Label><Input type="number" min="1" value={f.external_seats} onChange={set("external_seats")} placeholder="illimitati" className="mt-1" data-testid="seminar-seats"/></div>
              <div><Label className="text-xs">Pranzo (€)</Label><Input type="number" step="0.01" min="0" value={f.lunch_price} onChange={set("lunch_price")} placeholder="vuoto = no pranzo" className="mt-1" data-testid="seminar-lunch-price"/></div>
            </div>
            <div><Label className="text-xs">Descrizione</Label><Textarea rows={3} value={f.description} onChange={set("description")} className="mt-1" data-testid="seminar-description"/></div>
            <label className="flex items-center gap-3 p-3 rounded-lg bg-[#E2725B]/5 border border-[#E2725B]/20 text-sm cursor-pointer">
              <Switch checked={f.public_booking} onCheckedChange={(v) => setF({ ...f, public_booking: v })} data-testid="seminar-public-switch"/>
              <span>Pubblica nel catalogo online e apri le prenotazioni agli esterni</span>
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!f.name.trim() || invalidDates || save.isPending} onClick={() => save.mutate()} data-testid="seminar-save">
            {save.isPending ? "Salvataggio…" : "Salva seminario"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
